import React from 'react'

import { ToastProps } from './types'
import { Toast } from './Toast'
import styles from './index.module.scss'

/**
 * The lifecycle of this system is as follows:
 *
 * First, consumer calls `toasts.open`, which registers
 * a new toast component to state. A part of that creation
 * is adding a `setTimeout` to close the toast, which in this case
 * simply means changing the toasts `open` property to false
 * by calling {@link ExtendedToast.close}. All this happens
 * in the guts of the `toasts.open` function, which can be found
 * in {@link makeOpen}.
 *
 * Second, when the setTimeout expires, it sets `shouldUpdate` to true,
 * triggering a `React.useEffect`, which filters all toasts with
 * `isOpen = false` and overwrites the toasts array.
 * The guts of this functionality can be found in {@link makeCleanup}.
 *
 * This is all neatly wrapped up in a context pair; the Provider is
 * {@link ToastAnchor} and the consumer hook is {@link useToasts}.
 */
const Context = React.createContext<ToastContext>({
  open: () => {},
})

/**
 * Available methods:
 * - open - Create a new toast notification
 *
 * See {@link ToastContext}
 */
export const useToasts = () => React.useContext<ToastContext>(Context)

/**
 * Handles all the state surrounding toasts.
 * Renders the provider of the Toasts context as well as
 * any children passed in.
 */
export const ToastAnchor: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [shouldUpdate, setShouldUpdate] = React.useState<boolean>(false)
  const [toasts, setToasts] = React.useState<ExtendedToast[]>([])

  /**
   * Creates a new toast notification.
   */
  const open = React.useCallback(makeOpen(toasts, setToasts, setShouldUpdate), [
    toasts,
  ])

  /**
   * Cleans out cloased toasts.
   */
  React.useEffect(
    makeCleanup(toasts, setToasts, shouldUpdate, setShouldUpdate),
    [toasts, shouldUpdate],
  )

  return (
    <>
      <Context.Provider value={{ open }}>
        <ul id="toast-anchor" className={styles.ToastAnchor}>
          {toasts.map((toast) => (
            <Toast key={toast.id} {...toast} />
          ))}
        </ul>

        {children}
      </Context.Provider>
    </>
  )
}

/* ===== UTILITIES ===== */

/**
 * Function factory -- The result of this function will be passed
 * into `React.useCallback` to be used by the consumer of the
 * useToasts hook to create a new toast.
 *
 * @param toasts
 * All toasts that have been created.
 * @param setToasts
 * Set state with a new toast array.
 * @param setShouldUpdate
 * Signify to React that there was a change.
 */
function makeOpen(
  toasts: ExtendedToast[],
  setToasts: (toasts: ExtendedToast[]) => void,
  setShouldUpdate: (shouldUpdate: boolean) => void,
): (toast: ToastProps) => void {
  return function (toast) {
    const newToast = extendToast(toast)

    /**
     * Significance of 3800 seconds:
     *
     * The animation for Toasts involves a .4s slide in,
     * 3s of read time,
     * and .4s slide out. This equates to 3800ms total animation time,
     * and we want to ensure the element exists for the full animation
     * and no longer. This value matches what is set in the CSS file.
     */
    setTimeout(() => {
      newToast.close()
      setShouldUpdate(true)
    }, 3800)

    setToasts([newToast, ...toasts])
  }
}

/**
 * Function factory -- The result of this function will be passed
 * into React.useEffect to be run whenever `shouldUpdate` or `toasts`
 * changes.
 *
 * @param toasts
 * Current array of toasts.
 * @param setToasts
 * Update state.
 * @param shouldUpdate
 * Do we need to clean up state?
 * @param setShouldUpdate
 * Signify to ToastAnchor whether there has been a relevant change.
 */
function makeCleanup(
  toasts: ExtendedToast[],
  setToasts: (toasts: ExtendedToast[]) => void,
  shouldUpdate: boolean,
  setShouldUpdate: (shouldUpdate: boolean) => void,
): () => void {
  return function () {
    if (shouldUpdate) {
      setShouldUpdate(false)
      setToasts(toasts.filter((toast) => toast.isOpen))
    }
  }
}

/**
 * Ensures that the default toast is extended with
 * all internally necessary information.
 *
 * @param toast
 * Default toast props to be extended.
 */
function extendToast(toast: ToastProps): ExtendedToast {
  const id = (Math.random() + 1).toString(36)

  return Object.assign({}, toast, {
    id,
    isOpen: true,
    close() {
      this.isOpen = false
    },
  })
}

interface ExtendedToast extends ToastProps {
  /**
   * Randomized string identifier.
   */
  id: string
  /**
   * Is the toast currently visible?
   * Default `true`.
   */
  isOpen: boolean

  /**
   * Make toast.open `false`.
   */
  close(): void
}

interface ToastContext {
  /**
   * Open a new toast notification.
   *
   * @param toast
   * {@link ToastProps}
   */
  open(toast: ToastProps): void
}
